import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ClientCtx = createContext(null);

// Fallback shape until /api/clients returns — keeps AppShell from rendering an
// empty sidebar during the initial bootstrap.
const FALLBACK_CLIENT = {
  slug: "starlink",
  name: "Starlink",
  kind: "ticketing",
  brand_color: "#4F46E5",
  modules: [
    "dashboard", "tickets", "customers",
    "pocs", "knowledge_base", "reports", "settings",
  ],
};

/** Reads the currently-active client slug from localStorage. */
export function getActiveClientSlug() {
  return localStorage.getItem("kippu_client_id") || "";
}

export function ClientProvider({ children }) {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [activeSlug, setActiveSlug] = useState(() => {
    return localStorage.getItem("kippu_client_id") || "";
  });
  const [loading, setLoading] = useState(true);

  const active = useMemo(
    () => clients.find((c) => c.slug === activeSlug) || null,
    [clients, activeSlug]
  );

  // Refresh the list of clients whenever we have a user. If the persisted
  // active slug no longer matches, fall back to the server-recommended one.
  const refresh = useCallback(async () => {
    if (!user) {
      setClients([]);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/clients");
      // Defensive: some legacy endpoints returned a raw array. Only accept the
      // v1.4 multi-tenant shape and reject legacy customer-account rows (they
      // don't have a `slug`).
      const raw = Array.isArray(data) ? data : (data?.clients || []);
      const list = raw.filter((c) => c && c.slug);
      setClients(list);
      const persisted = localStorage.getItem("kippu_client_id") || "";
      // Persisted (localStorage) is the source of truth — it reflects the
      // switcher click the user made most recently. Fall back to server hint
      // only when there's no local value.
      const preferred = persisted || data?.active_client_id || user.active_client_id || (list[0]?.slug || "");
      if (list.some((c) => c.slug === preferred)) {
        setActiveSlug(preferred);
        localStorage.setItem("kippu_client_id", preferred);
      } else if (list[0]) {
        setActiveSlug(list[0].slug);
        localStorage.setItem("kippu_client_id", list[0].slug);
      } else {
        setActiveSlug("");
        localStorage.removeItem("kippu_client_id");
      }
    } catch {
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const switchClient = useCallback(async (slug) => {
    if (!slug || slug === activeSlug) return;
    // Persist optimistically so the api interceptor picks it up on the next request.
    localStorage.setItem("kippu_client_id", slug);
    setActiveSlug(slug);
    try {
      await api.post("/clients/switch", { slug });
    } catch {
      // Persistence failure is non-fatal — the header keeps working.
    }
    // Tell AuthContext to re-resolve role + permissions for the new client.
    window.dispatchEvent(new Event("kippu-client-switched"));
  }, [activeSlug]);

  const hasModule = useCallback(
    (key) => {
      const modules = (active || FALLBACK_CLIENT).modules || [];
      return modules.includes(key);
    },
    [active]
  );

  const value = useMemo(() => ({
    clients,
    active: active || (loading ? null : FALLBACK_CLIENT),
    activeSlug,
    switchClient,
    hasModule,
    loading,
    refresh,
  }), [clients, active, activeSlug, switchClient, hasModule, loading, refresh]);

  return <ClientCtx.Provider value={value}>{children}</ClientCtx.Provider>;
}

export const useClient = () => useContext(ClientCtx);
